import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { validateTicket, getScannedCount } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

// Evento de hoy. Cambiar este id cuando cambie el evento activo.
const ACTIVE_EVENT_ID = 14; // Amistades Peligrosas y Cómplices — 31/07/2026

function Scanner() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [scannedCount, setScannedCount] = useState(null);
  const html5QrCodeRef = useRef(null);
  const processingRef = useRef(false);
  const resultTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isStaff = user?.role === 'admin' || user?.role === 'portero';

  const fetchCount = () => {
    getScannedCount(ACTIVE_EVENT_ID)
      .then(res => setScannedCount(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (!isStaff) {
      navigate('/login?redirect=/escanear');
      return;
    }
    startScanner();
    fetchCount();
    pollIntervalRef.current = setInterval(fetchCount, 5000); // sincroniza entre porteros cada 5s
    return () => {
      stopScanner();
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const extractCode = (text) => {
    if (text.includes('/v/')) return text.split('/v/').pop().trim();
    return text.trim();
  };

  const startScanner = async () => {
    try {
      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decodedText) => {
          if (processingRef.current) return;
          processingRef.current = true;
          await handleScan(extractCode(decodedText));
        },
        () => {}
      );
      setScanning(true);
    } catch (err) {
      setCameraError('No se pudo acceder a la camara. Verifica los permisos del navegador.');
    }
  };

  const stopScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
      html5QrCodeRef.current = null;
    }
    if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current);
  };

  const handleScan = async (code) => {
    try {
      const res = await validateTicket(code);
      setResult({
        status: 'valid',
        title: 'Acceso permitido',
        subtitle: res.data.attendee_name || '',
        color: '#00cc66',
      });
      // Actualiza el contador al instante (optimista) y confirma con el backend
      setScannedCount(prev => prev ? { ...prev, scanned: prev.scanned + 1 } : prev);
      fetchCount();
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setResult({
          status: 'used',
          title: 'Entrada ya usada',
          subtitle: err.response?.data?.used_at
            ? `Usada el ${new Date(err.response.data.used_at).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
            : 'Esta entrada ya fue canjeada',
          color: '#ff4444',
        });
      } else if (status === 410) {
        setResult({
          status: 'expired',
          title: 'Fuera de horario',
          subtitle: err.response?.data?.error || 'Esta entrada no es válida a esta hora',
          color: '#ff4444',
        });
      } else if (status === 404) {
        setResult({ status: 'error', title: 'QR no encontrado', subtitle: 'Codigo no valido', color: '#ffaa00' });
      } else if (status === 429) {
        setResult({ status: 'error', title: 'Demasiados escaneos seguidos', subtitle: 'Espera unos segundos e intenta de nuevo', color: '#ffaa00' });
      } else {
        // DEBUG TEMPORAL: mostramos el error técnico real en pantalla para diagnosticar
        setResult({
          status: 'error',
          title: `Error${status ? ' ' + status : ''}`,
          subtitle: err.response?.data?.error || err.message || 'Error desconocido',
          color: '#ffaa00',
        });
      }
    }

    // Vuelve a escanear tras 3 segundos
    resultTimeoutRef.current = setTimeout(() => {
      setResult(null);
      processingRef.current = false;
    }, 3000);
  };

  const handleManual = () => {
    const code = prompt('Introduce el codigo de la entrada (o la URL completa):');
    if (code?.trim()) {
      processingRef.current = false;
      handleScan(extractCode(code.trim()));
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <div style={{ padding: '1rem', maxWidth: '480px', margin: '0 auto' }}>

        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <h1 style={{ color: colors.text, fontSize: '1.4rem', fontWeight: 'bold', margin: '0 0 0.3rem' }}>
            Scanner de entradas
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: 0 }}>
            {scanning ? 'Apunta al codigo QR de la entrada' : 'Iniciando camara...'}
          </p>
        </div>

        {/* Contador de entradas escaneadas */}
        <div style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.accent}`,
          borderRadius: '12px',
          padding: '0.9rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.6rem',
        }}>
          <ConfirmationNumberIcon style={{ color: colors.accent, fontSize: 22 }} />
          {scannedCount ? (
            <p style={{ color: colors.text, margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>
              {scannedCount.scanned} <span style={{ color: colors.textSecondary, fontWeight: 'normal', fontSize: '0.9rem' }}>/ {scannedCount.total} entradas escaneadas</span>
            </p>
          ) : (
            <p style={{ color: colors.textSecondary, margin: 0, fontSize: '0.9rem' }}>Cargando contador...</p>
          )}
        </div>

        {cameraError ? (
          <div style={{
            backgroundColor: '#ff444422', border: '1px solid #ff4444',
            borderRadius: '12px', padding: '1.5rem', textAlign: 'center'
          }}>
            <ErrorIcon style={{ color: '#ff4444', fontSize: 40, marginBottom: '0.5rem' }} />
            <p style={{ color: '#ff4444', margin: 0, fontSize: '0.9rem' }}>{cameraError}</p>
            <p style={{ color: colors.textSecondary, fontSize: '0.8rem', marginTop: '0.5rem' }}>
              En iOS: Ajustes → Safari → Camara → Permitir
            </p>
          </div>
        ) : (
          <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', border: `2px solid ${colors.accent}` }}>
            <div id="qr-reader" style={{ width: '100%' }} />

            {/* Overlay resultado */}
            {result && (
              <div style={{
                position: 'absolute', inset: 0,
                backgroundColor: result.color + 'ee',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: '1.5rem', textAlign: 'center',
              }}>
                {result.status === 'valid'
                  ? <CheckCircleIcon style={{ fontSize: 80, color: '#fff', marginBottom: '0.8rem' }} />
                  : <CancelIcon style={{ fontSize: 80, color: '#fff', marginBottom: '0.8rem' }} />
                }
                <p style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.4rem', margin: '0 0 0.4rem' }}>
                  {result.title}
                </p>
                {result.subtitle && (
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', margin: 0 }}>
                    {result.subtitle}
                  </p>
                )}
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginTop: '1rem' }}>
                  Continuando en 3 segundos...
                </p>
              </div>
            )}
          </div>
        )}

        {/* Validacion manual */}
        <div style={{ marginTop: '1.2rem', textAlign: 'center' }}>
          <button
            onClick={handleManual}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${colors.border}`,
              color: colors.textSecondary,
              borderRadius: '8px',
              padding: '0.6rem 1.2rem',
              cursor: 'pointer',
              fontSize: '0.85rem',
              width: '100%',
            }}
          >
            Introducir codigo manualmente
          </button>
        </div>

        {/* Info */}
        <div style={{
          marginTop: '1rem', backgroundColor: colors.bgSecondary,
          borderRadius: '12px', padding: '1rem',
          border: `1px solid ${colors.border}`
        }}>
          <p style={{ color: colors.textSecondary, fontSize: '0.8rem', margin: '0 0 0.4rem', fontWeight: 'bold' }}>
            Como usar:
          </p>
          <p style={{ color: colors.textSecondary, fontSize: '0.8rem', lineHeight: 1.5, margin: 0 }}>
            1. Apunta la camara al QR del comprador<br />
            2. Verde = entrada valida, acceso permitido<br />
            3. Rojo = ya usada, denegar acceso<br />
            4. El escaner continua automaticamente
          </p>
        </div>

      </div>
    </div>
  );
}

export default Scanner;